import { webPaths } from "@zplanem/web-paths"
import { useEffect, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import { PropertyChat } from "../../components/chat/property-chat"
import { ApiError } from "../../lib/api-client"
import { JournalList } from "../components/journal/journal-list"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { PageHeader } from "../components/page-header"
import { PropertyBottomNav } from "../components/property-bottom-nav"
import { PropertyTopBar } from "../components/property-top-bar"
import { ShareModal } from "../components/share-modal"
import { useLastPropertyId } from "../hooks/use-last-property-id"
import { useProperty } from "../hooks/use-property"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

/**
 * Journal tab of the property shell. Mirrors the chrome of
 * `property-detail.tsx` (mobile top bar + bottom nav + share modal +
 * floating PropertyChat) but swaps the main content for
 * `<JournalList />`.
 *
 * Note: the shared-chat rail toggle is intentionally omitted here. The
 * journal is a read-only feed view and the shared chat rail would
 * compete for the right side of the screen with the activity stream.
 * The `chatRailOpen` / `onChatRailToggle` props on `<PropertyTopBar>` /
 * `<PageActions>` are optional, so omitting them hides the toggle.
 */
export function PropertyJournalPage() {
	const { id } = useParams<{ id: string }>()
	const { data: property, isLoading, error } = useProperty(id)
	const { setLastPropertyId } = useLastPropertyId()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const [shareOpen, setShareOpen] = useState(false)

	const isNotFound = !id || (error instanceof ApiError && error.status === 404)

	useEffect(() => {
		if (property) setLastPropertyId(property.id)
		else if (isNotFound) setLastPropertyId(null)
	}, [property, isNotFound, setLastPropertyId])

	if (isNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	if (error) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-destructive px-6 text-center">
				{error.message}
			</div>
		)
	}
	if (!property) return null

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				onShareClick={() => setShareOpen(true)}
			/>
			<PageContainer>
				<PageActionsBar>
					<PageActions
						property={property}
						onShareClick={() => setShareOpen(true)}
					/>
				</PageActionsBar>
				<PageHeader
					eyebrow={property.name}
					eyebrowHref={webPaths.property(property.id)}
					title={t.properties.tabs.journal}
				/>
				<JournalList propertyId={property.id} />
			</PageContainer>
			<PropertyBottomNav propertyId={property.id} />
			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
			<PropertyChat propertyId={property.id} />
		</div>
	)
}
